import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EmbeddedViewRef,
  Injectable,
  NgZone,
} from '@angular/core';
import type { LngLatLike, MapboxGeoJSONFeature, Popup } from 'mapbox-gl';
import { CellGeoData } from './geocoder-cache';
import { ORI_LAYER } from './layer-store.service';
import { MapBoxService } from '../mapbox.service';
import { schoolsPoiLayers } from '../../menu/right-menu/layers-menu/education/education.service';
import { MapHttpService } from './map-http.service';
import { DatePipe } from '@angular/common';
import { BreakpointObserverService } from '../../../shared/services/breakpoint-observer.service';
import { Feature, Position } from 'geojson';
import {
  DEFAULT_POPUP,
  MapPopupComponent,
  ORI_POPUP,
  PopupData,
  SCHOOLS_POPUP,
} from '../visualization/map-popup/map-popup.component';
import { OVERVIEW_TAB_ID } from '../visualization/map-popup/popup-groups';
import { SearchGeocoderService } from '../../../shared/services/search-geocoder.service';
import { SelectedCellService } from './selected-cell.service';
import { pointsLayersList } from '../../menu/right-menu/layers-menu/points.service';
import { ReportsService } from '../../../reports/reports.service';
import { geocoderContextToString } from '../../../shared/util/geocoder-context-to-string';
import { convertLayerIdToCellType } from '../../../shared/util/layerIdToCellType';
import { Router } from '@angular/router';
import { ReportTypes } from '../../../user/user/user.model';
import {
  DESKTOP_POPUP_WIDTH,
  PopupPlacementService,
} from './popup-placement.service';

declare const mapboxgl: typeof import('mapbox-gl');

/** Elements a drag may start from — see `enableDragOnPopup`. */
const DRAG_HANDLE_SELECTOR = '.drag-button, .place';

/**
 * On the card while it is being moved. The point of moving it is to see what
 * it covers, so it stops being opaque for as long as it is in flight - see
 * `.mapboxgl-popup.default-popup.is-dragging` in map.component.scss.
 */
const DRAGGING_CLASS = 'is-dragging';

@Injectable({
  providedIn: 'root',
})
export class PopupService {
  public popupTitle!: string;
  public popup!: Popup;

  public isSeeMoreActive: boolean = true;

  /**
   * The grouped list's last open tab. Kept here because each click builds a
   * new card, and comparing cells tab by tab is the point of clicking another.
   */
  public selectedTabId = OVERVIEW_TAB_ID;

  public currentlySelectedId: string | null = null;
  /**
   * The layer the open card was opened from - a level, the arbitrary cell, or
   * one of the point layers. Not `LEVEL_LAYER`: it was only ever cast to that,
   * and a school card would have made the cast a lie.
   */
  public currentlySelectedLevel: string | null = null;
  public popupComponentRef?: ComponentRef<MapPopupComponent>;

  constructor(
    private mapboxService: MapBoxService,
    private mapHttpService: MapHttpService,
    private datePipe: DatePipe,
    private isMobileService: BreakpointObserverService,
    private geocoderService: SearchGeocoderService,
    private selectedCellService: SelectedCellService,
    private appRef: ApplicationRef,
    private reportsService: ReportsService,
    private router: Router,
    private placement: PopupPlacementService,
    private zone: NgZone
  ) {}

  public handlePopup(
    feature: MapboxGeoJSONFeature,
    coordinates: LngLatLike
  ): void {
    if (this.popup && this.popup.isOpen()) {
      this.popup.remove();
    }

    this.currentlySelectedId = feature.properties!.external_id;
    this.currentlySelectedLevel = feature.layer.id;

    const data: PopupData = {
      type: this.getPopupType(feature),
      properties: { ...feature.properties, ...feature.state },
      feature: feature,
    };

    if (pointsLayersList.includes(feature.layer.id)) {
      this.handlePointsPopup(feature);
    }

    this.initPopup(coordinates);
    this.setPopupContent(data);
  }

  public getPopupType(feature: MapboxGeoJSONFeature): PopupData['type'] {
    return schoolsPoiLayers.includes(feature.layer.id)
      ? SCHOOLS_POPUP
      : feature.layer.id === ORI_LAYER
        ? ORI_POPUP
        : DEFAULT_POPUP;
  }

  public initPopup(coordinates: mapboxgl.LngLatLike): void {
    const isMobile = this.isMobileService.isMobile.getValue();

    // Assign popup instance on first use with params related to mobile/desktop
    // as they are not changebale after init and we want to keep single popup instance
    // to manipulate it from geocoderService
    if (!this.popup) {
      this.popup = isMobile
        ? new mapboxgl.Popup({
            anchor: 'top',
            offset: [0, 0],
            // Widened from 345px: the popup now carries a tab strip and a
            // two-up stat grid, both of which were truncating at the old width.
            maxWidth: 'min(96vw, 520px)',
            className: 'default-popup',
            closeButton: false,
            closeOnClick: false,
          })
        : new mapboxgl.Popup({
            maxWidth: `${DESKTOP_POPUP_WIDTH}px`,
            className: 'default-popup',
            closeButton: false,
            closeOnClick: false,
          });
    }

    // The card is placed in screen pixels by `PopupPlacementService` -
    // `.default-popup` drops mapbox's transform - so this coordinate no longer
    // decides where it lands. GL JS still needs one to consider the popup
    // placeable, and on a phone it is what the map centres on.
    this.popup.setLngLat(coordinates);

    if (isMobile) {
      this.centerPopupOnMobile(coordinates);
    }
  }

  private centerPopupOnMobile(coordinates: LngLatLike): void {
    const map = this.mapboxService.map;
    if (map) {
      // Outside the zone: the map is built outside it, but a camera animation
      // started from in here inherits *this* zone for its whole flight, and
      // GL JS drives that flight with requestAnimationFrame - a patched one,
      // so every frame of it would run application-wide change detection.
      this.zone.runOutsideAngular(() =>
        map.flyTo({
          center: coordinates,
          zoom: map.getZoom(),
        })
      );
    }
  }

  public handlePopupTitle(feature?: MapboxGeoJSONFeature): void {
    this.setTitle('');

    const selectedFeature =
      feature ?? this.selectedCellService.getSelectedFeature();

    const id = selectedFeature?.id;
    const center = this.mapboxService.getFeatureCenter(selectedFeature!);

    if (!this.geocoderService.geocoderCache.getCellData(id)) {
      this.handleGeocoderRequest(id, center).then((res) => {
        if (res) {
          this.geocoderService.geocoderCache.addCell(res.id, res.data);
        }
        // Unconditional: without an address `updateTitle` falls back to the
        // feature's own name, which is what the cached branch below shows
        // anyway. Skipping it on failure left the popup title blank forever.
        this.updateTitle(selectedFeature!);
      });
    } else {
      this.updateTitle(selectedFeature!);
    }
  }

  public reapplyCssPosition(): void {
    const el = this.popup?.getElement() as HTMLElement | null;
    if (!el) return;
    requestAnimationFrame(() => this.placement.place(el));
  }

  private setPopupContent(data: PopupData): void {
    this.popupComponentRef = createComponent(MapPopupComponent, {
      environmentInjector: this.appRef.injector,
    });

    this.popupComponentRef.instance.data = data;

    this.appRef.attachView(this.popupComponentRef.hostView);

    const domElem = (this.popupComponentRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;

    this.popup.setDOMContent(domElem).addTo(this.mapboxService.map);

    const popupEl = this.popup.getElement() as HTMLElement;

    this.portalOutOfMapLayout(popupEl);

    // The card is positioned on every resize, not just on open: switching tabs
    // or expanding a list changes its height, and the clamp that keeps it
    // inside the map has to run against the new one.
    //
    // The map container is watched by the same observer because the card is
    // placed in viewport pixels now (see `portalOutOfMapLayout`): a container
    // that resizes or shifts - the right menu opening, the onboarding bar
    // appearing, an orientation change - no longer carries the card with it.
    const ro = new ResizeObserver(() => {
      const el = this.popup.getElement() as HTMLElement | null;
      if (el) this.placement.place(el);
    });

    ro.observe(popupEl);
    ro.observe(this.mapboxService.map.getContainer());

    this.popup.on('close', () => {
      ro.disconnect();
      this.appRef.detachView(this.popupComponentRef!.hostView);
      this.popupComponentRef!.destroy();
    });

    this.enableDragOnPopup();
  }

  /**
   * Lifts the card out of the map page's `.layout` and onto the page host.
   *
   * The mobile bottom bar is a fixed sibling of `<main>`, and `.layout` is
   * `position: fixed` - a stacking context - so every overlay inside the map
   * is sealed under whatever level that one box sits at, and no z-index on the
   * card could ever lift it over the bar. One step out, on `<app-map>`, the
   * card is a plain fixed box in the root stacking context and its z-index
   * means something again (map.component.scss draws the wrapper: `.layout` is
   * the only stacking context in the way, not the component boundary, so the
   * page's own stylesheet still reaches it).
   *
   * The page host rather than the body: the map route is kept alive by
   * `CustomRouteReuseStrategy`, which detaches this subtree rather than
   * destroying it. On the body the card would have no owner to be detached
   * with, and an open popup would go on floating over whatever page came next.
   *
   * GL JS builds this element fresh on every open, so the move belongs on the
   * open path rather than at startup. Removal is unaffected: `Popup.remove()`
   * detaches the element from whatever parent it has.
   */
  private portalOutOfMapLayout(popupEl: HTMLElement): void {
    // Null on any other map (reports, comparison), which has no bottom bar to
    // clear and is left exactly where GL JS put it.
    const pageEl = this.mapboxService.map.getContainer().closest('app-map');

    pageEl?.appendChild(popupEl);
  }

  /**
   * Makes the popup draggable by its handle or its bare background.
   *
   * Pointer events rather than a mouse pair plus a touch pair. The two
   * duplicated implementations had drifted — a fix to the mouse path silently
   * missed the touch one — and the touch half depended on `document` listeners
   * surviving a finger that slides off the handle, which is exactly what
   * `setPointerCapture` is for. One path now serves mouse, touch and pen, and
   * whatever is true of dragging on a laptop is true on a phone.
   *
   * A touch drag may only start somewhere that declares `touch-action: none`,
   * which the handle and the title do and `.popup-container` cannot: it is the
   * scroll parent of the tab panel, and `none` there would stop the panel
   * scrolling. `preventDefault()` on `pointerdown` is not a substitute — unlike
   * the `touchstart` handler this replaced, it does not suppress scrolling — so
   * the bare background stays a mouse-and-pen affordance.
   */
  private enableDragOnPopup() {
    const popupEl = this.popup.getElement() as HTMLElement;
    if (!popupEl) return;

    // The popup component renders on Angular's next tick, so the handle does
    // not exist yet on the frame the popup is added. Returning early here left
    // the popup permanently undraggable whenever that tick ran late.
    const attach = (attempt = 0): void => {
      const containerEl = popupEl.querySelector(
        '.popup-container'
      ) as HTMLElement | null;

      if (!containerEl) {
        if (attempt < 10) requestAnimationFrame(() => attach(attempt + 1));
        return;
      }

      const map = this.mapboxService.map;
      const mapContainer = map.getContainer() as HTMLElement;

      let pointerId: number | null = null;
      let dragOffsetX = 0;
      let dragOffsetY = 0;

      // The card's own background and its handles, never the content: a drag
      // starting inside the scrolling panel has to scroll it, not move the
      // popup.
      const isDragOrigin = (evt: PointerEvent) => {
        const el = evt.target as Element | null;
        if (!el) return false;
        if (el.closest(DRAG_HANDLE_SELECTOR)) return true;

        // See the note above: the background cannot opt out of the browser's
        // touch gestures, so a finger starting there would pull-to-refresh and
        // cancel the drag rather than move the card.
        return evt.pointerType !== 'touch' && el === containerEl;
      };

      const onPointerMove = (evt: PointerEvent) => {
        if (pointerId === null || evt.pointerId !== pointerId) return;
        evt.preventDefault();

        // On the first move rather than on the press: the card's bare
        // background is also a drag origin, so a plain click on it would
        // otherwise flash the whole panel translucent and back.
        popupEl.classList.add(DRAGGING_CLASS);

        const mapRect = mapContainer.getBoundingClientRect();
        this.placement.dragTo(
          popupEl,
          evt.clientX - mapRect.left - dragOffsetX,
          evt.clientY - mapRect.top - dragOffsetY
        );
      };

      const endDrag = (evt: PointerEvent) => {
        if (pointerId === null || evt.pointerId !== pointerId) return;

        if (popupEl.hasPointerCapture(pointerId)) {
          popupEl.releasePointerCapture(pointerId);
        }

        pointerId = null;
        popupEl.style.cursor = 'unset';
        popupEl.classList.remove(DRAGGING_CLASS);
      };

      const onPointerDown = (evt: PointerEvent) => {
        // Dragging while the map eases would fight mapbox for the popup's
        // position; a second finger is a map gesture, not a drag.
        if (
          pointerId !== null ||
          !evt.isPrimary ||
          map.isMoving() ||
          !isDragOrigin(evt)
        ) {
          return;
        }

        evt.preventDefault();
        popupEl.style.cursor = 'move';

        const popupRect = popupEl.getBoundingClientRect();
        dragOffsetX = evt.clientX - popupRect.left;
        dragOffsetY = evt.clientY - popupRect.top;
        pointerId = evt.pointerId;

        // Capture keeps the moves coming to this element once the finger
        // leaves it, which is what the old document-level listeners were
        // approximating. It throws if the pointer is no longer active, which
        // is not a reason to abandon the drag — uncaptured moves still arrive
        // while the pointer is over the popup.
        try {
          popupEl.setPointerCapture(evt.pointerId);
        } catch {
          /* not capturable; drag continues without it */
        }
      };

      // The uncaptured drag above is the one that can get stuck: its moves stop
      // arriving the moment the pointer leaves the popup, and with no
      // `pointerup` bound outside it nothing would ever clear `pointerId`. The
      // card would keep the `move` cursor and follow every later hover. Losing
      // the capture mid-drag lands in the same state, so it ends the drag too.
      const onPointerLeave = (evt: PointerEvent) => {
        if (pointerId !== null && popupEl.hasPointerCapture(pointerId)) return;
        endDrag(evt);
      };

      // Bound on the outer element only: everything inside bubbles to it, and
      // capturing there keeps `currentTarget` unambiguous for the release.
      popupEl.addEventListener('pointerdown', onPointerDown);
      popupEl.addEventListener('pointermove', onPointerMove);
      popupEl.addEventListener('pointerup', endDrag);
      popupEl.addEventListener('pointercancel', endDrag);
      popupEl.addEventListener('pointerleave', onPointerLeave);
      popupEl.addEventListener('lostpointercapture', endDrag);
    };

    requestAnimationFrame(() => attach());
  }

  public updatePopupContent(data: PopupData): void {
    if (this.popupComponentRef) {
      // Concat existing data with updated, reassign of data runs change detection
      this.popupComponentRef.instance.data = {
        type: data.type,
        properties: {
          ...this.popupComponentRef.instance.data.properties,
          ...data.properties,
        },
        feature: data.feature,
      };
      return;
    }
  }

  private async handleGeocoderRequest(
    featureID: Feature['id'],
    coordinates: Position
  ): Promise<{
    id: Feature['id'];
    data: CellGeoData;
  } | null> {
    const data = await this.geocoderService.reverseGeocodeViaPlugin(
      coordinates[1],
      coordinates[0]
    );

    return data ? { id: featureID, data } : null;
  }

  private updateTitle(feature: MapboxGeoJSONFeature): void {
    const id = feature.id;
    const levelLayer = feature.layer.id;
    const defaultName = feature!.properties!.name;
    const cellData = this.geocoderService.geocoderCache.getCellData(id);

    if (!cellData) {
      return;
    }

    let title = geocoderContextToString(cellData, levelLayer, defaultName);

    // Fallback to coordinates if no title could be constructed
    if (!title.trim()) {
      title = `lng: ${cellData.center[0].toFixed(
        2
      )} lat: ${cellData.center[1].toFixed(2)}`;
    }

    feature.properties!.addressToCompare = title;
    this.setTitle(title);
  }

  private handlePointsPopup(feature: MapboxGeoJSONFeature): void {
    this.popup.once('open', () => {
      this.assignSelectedStatus(true, feature);
    });

    this.popup.once('close', () => {
      this.assignSelectedStatus(false, feature);
    });
  }

  private assignSelectedStatus(
    status: boolean,
    feature: MapboxGeoJSONFeature
  ): void {
    this.mapboxService.map.setFeatureState(feature, { selected: status });
  }

  private setTitle(title: string): void {
    this.popupTitle = title;

    // Also set title for selected location in reports
    this.reportsService.selectedLocation = title;
  }

  public downloadXlsx(): void {
    if (!this.currentlySelectedId || !this.currentlySelectedLevel) {
      return;
    }

    this.mapHttpService
      .downloadXlsx(
        this.currentlySelectedId,
        convertLayerIdToCellType(this.currentlySelectedLevel)!
      )
      .subscribe((data) => {
        const currentTime = this.datePipe.transform(
          new Date(),
          'yyyy-MM-dd HH:mm:ss'
        );

        const blob: Blob = data as Blob;
        let link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `${this.currentlySelectedLevel}_${
          this.currentlySelectedId
        }_${currentTime}.xlsx`;
        link.click();
      });
  }

  public toggleSeeMore(): void {
    this.isSeeMoreActive = !this.isSeeMoreActive;
  }

  public handleRequestReport(): void {
    // The tile is the location and Community Compass is the report for it, so
    // there is nothing left to ask - open on the summary, where both are still
    // editable.
    this.reportsService.enterOrderFlow({
      at: 'review',
      type: ReportTypes.COMMUNITY_COMPASS,
      geometry: 'cell',
    });

    this.router.navigate(['/reports/order'], { queryParamsHandling: 'merge' });
  }

  public handleClose(): void {
    this.selectedCellService.deselectCell();
    this.popup.remove();
  }
}
